import React, { useMemo } from "react";
import { Box, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, type SxProps, type Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { resolveMediaUrl } from "../utils/mediaUrl";

interface Education {
  degree: string;
  institution: string;
  branch: string;
  scoreOrGpa: string;
  year: string;
}

interface SkillWithLevel {
  skill: string;
  expertise: string;
  type?: string;
}

interface Project {
  name: string;
  description: string;
  link?: string;
}

export interface ResumeData {
  name: string;
  profilePicUrl?: string;
  primaryEmail?: string;
  primaryContact?: string;
  objective?: string;
  github?: string;
  leetcode?: string;
  hackerrank?: string;
  primarySkills?: string[];
  subjectScores?: Record<string, number | string | null | undefined>;
  skillsWithLevel?: SkillWithLevel[];
  skillsVerified?: boolean;
  education?: Education[];
  projects?: Project[];
  primaryCertificates?: string[];
  secondaryCertificates?: string[];
  hobbies?: string[];
}

const toDisplayScore = (value: unknown): string => {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}%`;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "--") return "N/A";
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? `${parsed}%` : trimmed;
  }
  return "N/A";
};

const normalizeSubjectScoreMap = (
  raw: Record<string, number | string | null | undefined> | undefined
): { columns: string[]; scoresByColumn: Record<string, string> } => {
  const scoresByColumn: Record<string, string> = {};
  const columns: string[] = [];
  Object.entries(raw ?? {}).forEach(([key, value]) => {
    const label = key.trim();
    if (!label) return;
    columns.push(label);
    scoresByColumn[label] = toDisplayScore(value);
  });
  return { columns, scoresByColumn };
};

interface StudentResumeViewProps {
  data: ResumeData;
  className?: string;
}

const compactCellSx = {
  fontSize: 10,
  py: "3px",
  px: "8px",
};

const ResumeSectionHeading: React.FC<{ title: string; headingSx: SxProps<Theme> }> = ({ title, headingSx }) => (
  <Typography component="div" sx={headingSx}>
    {title}
  </Typography>
);

const StudentResumeView: React.FC<StudentResumeViewProps> = ({ data, className = "" }) => {
  const theme = useTheme();
  const resumeAccent = theme.palette.mode === "dark" ? theme.palette.primary.light : "#0f3460";
  const resumeMuted = theme.palette.mode === "dark" ? theme.palette.divider : "#c5cae9";

  const sectionHeadingSx = useMemo(
    () => ({
      fontSize: 11,
      fontWeight: 700,
      color: resumeAccent,
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
      mb: 0.5,
      pb: 0.5,
      borderBottom: `1px solid ${resumeMuted}`,
    }),
    [resumeAccent, resumeMuted],
  );

  const personal = {
    firstName: data.name?.split(/\s+/)[0] ?? "",
    lastName: data.name?.split(/\s+/).slice(1).join(" ") ?? "",
    primaryEmail: data.primaryEmail,
    primaryContact: data.primaryContact,
  };
  const { columns: subjectScoreColumns, scoresByColumn: subjectScores } = useMemo(
    () => normalizeSubjectScoreMap(data.subjectScores),
    [data.subjectScores]
  );
  const hasSubjectScores = useMemo(
    () => subjectScoreColumns.length > 0 && Object.values(subjectScores).some((value) => value !== "N/A"),
    [subjectScoreColumns, subjectScores]
  );
  const secondarySkillsRows = data.skillsWithLevel ?? [];
  const hasSecondaryTable = secondarySkillsRows.length > 0;
  const educationList =
    data.education?.map((e) => ({
      degree: e.degree,
      institute: e.institution,
      branch: e.branch,
      cgpa: e.scoreOrGpa,
      yop: e.year,
    })) ?? [];
  const projects =
    data.projects?.map((p) => ({
      type: p.name || "Project",
      details: p.description,
      link: p.link,
    })) ?? [];
  const primaryCerts = (data.primaryCertificates ?? []).map((name) => ({ name }));
  const secondaryCerts = (data.secondaryCertificates ?? []).map((name) => ({ name }));
  const hobbiesText = data.hobbies?.length ? data.hobbies.join(", ") : "";

  const hasEducation = educationList.some((e) => e.institute);
  const hasProjects = projects.some((p) => p.details);
  const resumeObjective = data.objective;

  return (
    <Box className={className} sx={{ bgcolor: "background.default", width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          fontSize: 11,
          lineHeight: 1.4,
          color: "text.secondary",
          bgcolor: "background.paper",
          fontFamily: "Arial, Helvetica, sans-serif",
          width: "100%",
          maxWidth: "100%",
          mx: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box className="d-flex justify-content-between align-items-start mb-2">
          <Box className="flex-grow-1 min-w-0">
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: resumeAccent, mb: 0.5 }}>
              {[personal.firstName, personal.lastName].filter(Boolean).join(" ") || data.name || "Your Name"}
            </Typography>
            {(personal.primaryEmail || personal.primaryContact) && (
              <Typography variant="body2" color="text.secondary" className="mb-1">
                {personal.primaryEmail && <span>{personal.primaryEmail}</span>}
                {personal.primaryEmail && personal.primaryContact && <span className="text-muted mx-1">|</span>}
                {personal.primaryContact && <span>{personal.primaryContact}</span>}
              </Typography>
            )}
          </Box>
          {data.profilePicUrl && (
            <Box
              component="img"
              src={data.profilePicUrl ? resolveMediaUrl(data.profilePicUrl) : data.profilePicUrl}
              alt="Profile"
              sx={{
                width: 70,
                height: 70,
                objectFit: "cover",
                borderRadius: "50%",
                border: "2px solid",
                borderColor: resumeAccent,
                flexShrink: 0,
                ml: 2,
              }}
            />
          )}
        </Box>

        {resumeObjective && (
          <Box sx={{ mb: 3 }}>
            <ResumeSectionHeading title="Objective" headingSx={sectionHeadingSx} />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", mb: 0 }}>
              {resumeObjective}
            </Typography>
          </Box>
        )}

        {!resumeObjective && (
          <Divider
            sx={{
              borderColor: resumeMuted,
              borderWidth: 1,
              opacity: 1,
              my: 1.5,
            }}
          />
        )}

        {(hasSubjectScores || hasSecondaryTable) && (
          <Box className="mb-3">
            {hasSubjectScores && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.5,
                  pb: 0.5,
                  borderBottom: `1px solid ${resumeMuted}`,
                }}
              >
                <Box component="span" sx={{ color: "primary.main", fontWeight: 700, fontSize: 11, lineHeight: 1, flexShrink: 0 }}>
                  ✓
                </Box>
                <Typography
                  component="div"
                  sx={{
                    ...sectionHeadingSx,
                    mb: 0,
                    pb: 0,
                    borderBottom: "none",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  Primary Skills with Exskilence Scores
                </Typography>
              </Box>
            )}

            {hasSubjectScores && (
              <Table size="small" sx={{ mt: 0.5, "& th, & td": compactCellSx }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    {subjectScoreColumns.map((col) => (
                      <TableCell key={col} align="center" sx={{ color: resumeAccent, borderBottom: `1px solid ${resumeMuted}` }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {subjectScoreColumns.map((col) => (
                      <TableCell
                        key={`score-${col}`}
                        align="center"
                        title={subjectScores[col] === "N/A" ? "Not Applicable" : undefined}
                      >
                        {subjectScores[col]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {hasSecondaryTable && (
              <Box className="pt-2">
                <ResumeSectionHeading title="Secondary Skills" headingSx={sectionHeadingSx} />
                <Table size="small" sx={{ mt: 0.5, "& th, & td": compactCellSx }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ width: "39%", color: resumeAccent, borderBottom: `1px solid ${resumeMuted}` }}>
                      Secondary Skills
                    </TableCell>
                    <TableCell sx={{ width: "22%", color: resumeAccent, borderBottom: `1px solid ${resumeMuted}` }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ width: "39%", color: resumeAccent, borderBottom: `1px solid ${resumeMuted}` }}>
                      Expertise
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {secondarySkillsRows.map((skill, i) => (
                    <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? "transparent" : "action.hover" }}>
                      <TableCell className="text-break">{skill.skill}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{skill.type === 'non-tech' ? 'Non-Tech' : 'Tech'}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{skill.expertise}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}

        {hasEducation && (
          <Box className="mb-3">
            <ResumeSectionHeading title="Education" headingSx={sectionHeadingSx} />
            <Table size="small" sx={{ "& th, & td": compactCellSx }}>
              <TableHead>
                <TableRow className="border-bottom border-secondary">
                  {["Degree", "Institution", "Branch", "% / CGPA", "Year"].map((h) => (
                    <TableCell key={h} sx={{ textAlign: "left", color: resumeAccent, fontWeight: 600 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {educationList
                  .filter((e) => e.institute)
                  .map((edu, i) => (
                    <TableRow key={i} className="border-bottom border-light">
                      <TableCell className="text-break">{edu.degree}</TableCell>
                      <TableCell className="text-break">{edu.institute}</TableCell>
                      <TableCell className="text-break">{edu.branch || "—"}</TableCell>
                      <TableCell className="text-break">{edu.cgpa || "—"}</TableCell>
                      <TableCell className="text-break">{edu.yop || "—"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {hasProjects && (
          <Box className="mb-3">
            <ResumeSectionHeading title="Projects" headingSx={sectionHeadingSx} />
            {projects
              .filter((p) => p.details)
              .map((proj, i) => (
                <Box key={i} className="mb-2">
                  <Typography
                    component="span"
                    className="small fw-semibold mb-1 d-inline-block"
                    sx={{
                      bgcolor: "primary.light",
                      color: "primary.dark",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    {proj.link ? (
                      <Box
                        component="a"
                        href={proj.link}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: "primary.main", textDecoration: "none" }}
                      >
                        {proj.type}
                      </Box>
                    ) : (
                      proj.type
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div" dangerouslySetInnerHTML={{ __html: proj.details }} />
                </Box>
              ))}
          </Box>
        )}

        {(primaryCerts.some((c) => c.name) || secondaryCerts.some((c) => c.name)) && (
          <Box className="mb-3">
            <ResumeSectionHeading title="Certificates" headingSx={sectionHeadingSx} />
            {(() => {
              const primaryNames = primaryCerts.filter((c) => c.name).map((c) => c.name);
              const secondaryNames = secondaryCerts.filter((c) => c.name).map((c) => c.name);
              const hasP = primaryNames.length > 0;
              const hasS = secondaryNames.length > 0;
              if (hasP) {
                return (
                  <Box className="d-flex align-items-start gap-2 small" sx={{ color: "text.secondary" }}>
                    <Box sx={{ width: 16, flexShrink: 0 }}>
                      <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                        ✓
                      </Box>
                    </Box>
                    <Box className="flex-grow-1 min-w-0 text-break">
                      <Box className={hasS ? "mb-1" : ""}>
                        <strong>Primary: </strong>
                        {primaryNames.join(", ")}
                      </Box>
                      {hasS && (
                        <Box>
                          <strong>Secondary: </strong>
                          {secondaryNames.join(", ")}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              }
              if (hasS) {
                return (
                  <Typography variant="body2" className="text-break" color="text.secondary">
                    <strong>Secondary: </strong>
                    {secondaryNames.join(", ")}
                  </Typography>
                );
              }
              return null;
            })()}
          </Box>
        )}

        {hobbiesText && (
          <Box className="mb-3">
            <ResumeSectionHeading title="Hobbies & Interests" headingSx={sectionHeadingSx} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              {hobbiesText}
            </Typography>
          </Box>
        )}

        <Box className="mt-3 pt-2 border-top text-center" sx={{ borderColor: "divider" }}>
          <Typography variant="body2" color="text.secondary" className="small">
            <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
              ✓
            </Box>{" "}
            — Exskilence Verified
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default StudentResumeView;
